import React, { FC, useEffect, useState } from 'react'
import { useAuth } from '@frontegg/react'
import { useNavigate } from 'react-router-dom'
import { Box, CircularProgress, Grid } from '@mui/material'

/* Utils */
import { hasAnyRole, hasPermission, Permission, Role } from '../../../lib/utils'

/* Components */
import { Container, Wrapper } from './settings.styles'
import SettingsListItem from './SettingsListItem'

const defaultSettings = [
  {
    title: 'Account Management',
    description: 'Control account-wide configurations for everyone in your company.',
    link: '/settings/account-management',
    buttonText: 'Manage Account',
    permission: Permission.AccountManagementView
  },
  {
    title: 'User Management',
    description: 'Manage users, privileges, and roles.',
    link: '/settings/user-management',
    buttonText: 'Manage Users',
    permission: Permission.UsersProfileView
  },
  {
    title: 'Integrations',
    description: 'Configure & Edit your plugins for ticketing, endpoint detection and more.',
    link: '/settings/integrations/data-sources',
    buttonText: 'Manage Preferences',
    permission: Permission.ConfigIntegrationsConfigView
  },
  {
    title: 'Audit Log',
    description: 'View system performance insights and usage patterns.',
    link: '/settings/audit-log',
    buttonText: 'View Audit Log',
    permission: Permission.AuditView
  }
]

const Settings: FC = () => {
  const navigate = useNavigate()
  const { user, isLoading } = useAuth()
  const [settings, setSettings] = useState(defaultSettings)

  useEffect(() => {
    if (user) {
      if (hasAnyRole([Role.Viewer, Role.CPIManager], user)) {
        setSettings(defaultSettings.filter((n) => n.title !== 'User Management'))
      }
      if (hasAnyRole([Role.Admin, Role.Owner, Role.Demo], user)) {
        if (hasAnyRole([Role.Owner, Role.Admin], user)) {
          return setSettings(defaultSettings)
        }
        return setSettings(defaultSettings.filter((n) => n.title !== 'Account Management'))
      }
    }
  }, [user])

  const handleClick = (permission: Permission, link: string) => {
    if (!hasPermission(permission, user)) {
      return
    }

    navigate(link)
  }

  return (
    <Container>
      <Wrapper>
        <Grid container spacing={2}>
          {isLoading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            settings.map(({ title, description, link, buttonText, permission }) => (
              <Grid item xs={12} key={title}>
                <SettingsListItem
                  title={title}
                  description={description}
                  handleClick={() => handleClick(permission, link)}
                  buttonText={buttonText}
                  hasPermission={hasPermission(permission, user)}
                />
              </Grid>
            ))
          )}
        </Grid>
      </Wrapper>
    </Container>
  )
}

export default Settings
